import { z } from 'zod';
import { DOCUMENT_TYPES_BY_KEY, type DocStatus, type WorkflowAction } from './documents';
import { gratuityRulesSchema, leaveRulesSchema } from './hr';
import { always } from './schemas';

// ---------- Settings ----------

/** Rules the production scheduler can order work by; the order is a setting. */
export const PLANNING_PRIORITY_RULES = ['COMMITTED_DATE', 'RUSH', 'ADVANCE_PAID', 'VIP', 'ORDER_AGE'] as const;
export type PlanningPriorityRule = (typeof PLANNING_PRIORITY_RULES)[number];
export const PLANNING_PRIORITY_LABELS: Record<PlanningPriorityRule, string> = {
  COMMITTED_DATE: 'Earliest committed delivery date',
  RUSH: 'Rush orders first',
  ADVANCE_PAID: 'Advance received first',
  VIP: 'VIP customers first',
  ORDER_AGE: 'Oldest order first',
};

export interface SettingDef<T> {
  key: string;
  label: string;
  description: string;
  group: string;
  schema: z.ZodType<T>;
  default: T;
  /** Labels for each allowed value (dropdown settings). */
  choices?: Record<string, string>;
  /** Number settings: unit shown after the field. */
  unit?: string;
  /** Kept by a dedicated screen; not listed on the Settings page. */
  hidden?: boolean;
}

const setting = <T>(def: SettingDef<T>) => def;

/** Cost of stock held for an order: the company-wide average, or the order's own purchase price. */
export const ORDER_STOCK_COST_METHODS = ['AVERAGE', 'ORDER_PRICE'] as const;
export type OrderStockCostMethod = (typeof ORDER_STOCK_COST_METHODS)[number];
export const ORDER_STOCK_COST_LABELS: Record<OrderStockCostMethod, string> = {
  AVERAGE: 'Average cost (company-wide moving average)',
  ORDER_PRICE: "Order's own purchase price",
};
/** The method in force on a date (YYYY-MM-DD); average cost before the first choice. */
export function orderStockCostOn(history: readonly { from: string; method: OrderStockCostMethod }[], date: string): OrderStockCostMethod {
  let m: OrderStockCostMethod = 'AVERAGE';
  for (const h of [...history].sort((a, b) => a.from.localeCompare(b.from))) if (h.from <= date) m = h.method;
  return m;
}

/** Purchase & Inventory › Settings › Purchasing rules. */
export const purchasingRulesSchema = z.object({
  /** Inward gate pass approval (stored as the gate pass approval policy): off = recorded as approved on submit. */
  'inventory.igpApproval': z.boolean(),
  'inventory.overReceiptPct': z.coerce.number({ error: 'Enter a percentage' }).min(0, 'Cannot be negative').max(50, 'At most 50%'),
  'inventory.urgentPoLimit': z.coerce.number({ error: 'Enter an amount' }).min(0, 'Cannot be negative').max(10_000_000, 'Too large'),
  'inventory.poTerms': z.string().max(4000, 'At most 4000 characters'),
  'inventory.oldIssuesReturnFrom': z.string({ error: 'Choose a date' }).regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose a date'),
});
export type PurchasingRulesInput = z.infer<typeof purchasingRulesSchema>;
export const orderStockCostChangeSchema = z.object({
  method: z.enum(ORDER_STOCK_COST_METHODS, { error: 'Choose how held stock is costed' }),
  from: z.string({ error: 'Choose the month' }).regex(/^\d{4}-\d{2}-01$/, 'Choose the first day of a month'),
});
export type OrderStockCostChange = z.infer<typeof orderStockCostChangeSchema>;

/** Every system setting with its type and default. Unknown keys are rejected. */
export const SETTINGS = {
  'workflow.auditCheck': setting({
    key: 'workflow.auditCheck',
    label: 'Audit check after posting',
    description: 'Show an audit step on posted documents so a reviewer can mark them as checked. Replaces the old "Verification" step.',
    group: 'Workflow',
    schema: z.boolean(),
    default: false,
  }),
  'workflow.allowDraftDelete': setting({
    key: 'workflow.allowDraftDelete',
    label: 'Allow deleting drafts',
    description: 'Drafts that were never submitted can be deleted. Submitted documents can only be cancelled.',
    group: 'Workflow',
    schema: z.boolean(),
    default: true,
  }),
  'inventory.valuationMode': setting({
    key: 'inventory.valuationMode',
    label: 'Inventory accounting',
    description: 'Periodic: purchases are expensed and closing stock is posted at month-end. Perpetual: every stock movement posts to inventory accounts. Switching is done with a cut-over date in the Inventory module.',
    group: 'Inventory',
    schema: z.enum(['PERIODIC', 'PERPETUAL']),
    choices: { PERIODIC: 'Periodic', PERPETUAL: 'Perpetual' },
    default: 'PERIODIC' as 'PERIODIC' | 'PERPETUAL',
  }),
  'inventory.costMethod': setting({
    key: 'inventory.costMethod',
    label: 'Cost method',
    description: 'Moving weighted average per item.',
    group: 'Inventory',
    schema: z.literal('WEIGHTED_AVERAGE'),
    default: 'WEIGHTED_AVERAGE' as const,
  }),
  'inventory.overReceiptPct': setting({
    key: 'inventory.overReceiptPct',
    label: 'Receive more than ordered, up to',
    description: 'Goods receipts may take up to this much above the purchase order line, with a reason (shown in red everywhere). Item categories and sub-categories can set their own %; this one applies where they do not. Whole units are rounded down. Above it: a Sub PO for the extra, or the extra goes back. 0 = never more than ordered.',
    group: 'Purchasing',
    schema: z.coerce.number().min(0).max(50),
    unit: '%',
    default: 0 as number,
  }),
  'sales.productionCreditCheck': setting({
    key: 'sales.productionCreditCheck',
    label: 'Credit limit check on Send to production',
    description: 'When an order is sent to production: the customer\'s balance plus the order compared with their credit limit (customers without a limit are not checked). Warn shows a message and sends; Block stops it.',
    group: 'Sales',
    schema: z.enum(['OFF', 'WARN', 'BLOCK']),
    choices: { OFF: 'Off', WARN: 'Warn', BLOCK: 'Block' },
    default: 'OFF' as 'OFF' | 'WARN' | 'BLOCK',
  }),
  'inventory.poTerms': setting({
    key: 'inventory.poTerms',
    label: 'Purchase order terms and conditions',
    description: 'Printed at the foot of every purchase order sent to suppliers, one point per line. Empty = no terms section on the print.',
    group: 'Purchasing',
    schema: z.string().max(4000),
    // Empty until the company enters its own terms (owner, 2026-11-03): nothing is printed then.
    default: '' as string,
  }),
  'inventory.oldIssuesReturnFrom': setting({
    key: 'inventory.oldIssuesReturnFrom',
    label: 'Old-system issues can come back from',
    description: 'Return to store: a section may return what was issued to it, less what it already returned. Issues made in the old ERP on or after this date count too; earlier ones do not.',
    group: 'Purchasing',
    schema: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    default: '2026-08-01' as string,
  }),
  'inventory.urgentPoLimit': setting({
    key: 'inventory.urgentPoLimit',
    label: 'Urgent purchase order limit',
    description: 'Largest total (AED, including VAT) of an urgent purchase order made without a requisition or MRP. It is still approved as usual and listed in the exceptions report. 0 = urgent purchase orders not allowed.',
    group: 'Purchasing',
    schema: z.coerce.number().min(0).max(10_000_000),
    unit: 'AED',
    default: 0 as number,
  }),
  'inventory.orderStockCost': setting({
    key: 'inventory.orderStockCost',
    label: 'Cost of stock held for an order',
    description: 'How material bought for a customer order is costed to it, with the month each choice applies from. Changed only in Purchase & Inventory › Settings › Purchasing rules, from the start of a month.',
    group: 'Purchasing',
    schema: z.array(z.object({ from: z.string().regex(/^\d{4}-\d{2}-01$/), method: z.enum(ORDER_STOCK_COST_METHODS) })).max(120),
    default: [] as { from: string; method: OrderStockCostMethod }[],
    hidden: true,
  }),
  'payroll.dayRateBasis': setting({
    key: 'payroll.dayRateBasis',
    label: 'Day rate',
    description: 'Monthly salary divided by the calendar days of the month (old system) or by 30.',
    group: 'Payroll',
    schema: z.enum(['CALENDAR_DAYS', 'THIRTY']),
    choices: { CALENDAR_DAYS: 'Calendar days of the month', THIRTY: '30 days' },
    default: 'CALENDAR_DAYS' as 'CALENDAR_DAYS' | 'THIRTY',
  }),
  'payroll.deductionCap': setting({
    key: 'payroll.deductionCap',
    label: 'Deduction limit',
    description: 'Late / early leave and deductions (fines, damage, other) taken from one month\'s pay. Half (UAE law): a deduction that does not fit moves to next month. Advance and loan recoveries are not limited by this: they take what is due as long as net pay does not go below 0.',
    group: 'Payroll',
    schema: z.enum(['HALF', 'NONE']),
    choices: { HALF: 'At most half the month\'s pay (UAE law)', NONE: 'No limit (net pay never below 0)' },
    default: 'HALF' as 'HALF' | 'NONE',
  }),
  'payroll.startingSalaryApproval': setting({
    key: 'payroll.startingSalaryApproval',
    label: "New employee's first salary needs approval",
    description: 'On: the salary entered when an employee is added waits for approval by someone else (Salaries & Allowances › Pending), like any pay change. Off: it applies at once.',
    group: 'Payroll',
    schema: z.boolean(),
    default: true,
  }),
  'payroll.salaryChangeFinalAbovePercent': setting({
    key: 'payroll.salaryChangeFinalAbovePercent',
    label: 'Second approval for large pay changes (%)',
    description: 'A pay change that raises gross pay by more than this percentage needs a first approval and then a final approval by a different person. 0 switches the second approval off.',
    group: 'Payroll',
    schema: z.coerce.number().min(0).max(100),
    unit: '%',
    default: 15 as number,
  }),
  'payroll.overtimeApproval': setting({
    key: 'payroll.overtimeApproval',
    label: 'Overtime approval',
    description: 'Overtime is paid only once approved, per employee and month (Attendance › Overtime): by one approver, or by two different people (first and final). None: paid as worked, without approval.',
    group: 'Payroll',
    schema: z.enum(['NONE', 'ONE', 'TWO']),
    choices: { NONE: 'None (paid as worked)', ONE: 'One level', TWO: 'Two levels (first and final, different people)' },
    default: 'ONE' as 'NONE' | 'ONE' | 'TWO',
  }),
  'payroll.gratuity': setting({
    key: 'payroll.gratuity',
    label: 'Gratuity',
    description: 'End-of-service gratuity (UAE law by default) and its monthly provision.',
    group: 'Payroll',
    schema: gratuityRulesSchema,
    default: gratuityRulesSchema.parse({}),
    hidden: true,
  }),
  'leave.rules': setting({
    key: 'leave.rules',
    label: 'Leave rules',
    description: 'Annual leave earning and carry forward, sick / maternity / parental pay, sandwich rule.',
    group: 'Payroll',
    schema: leaveRulesSchema,
    default: leaveRulesSchema.parse({}),
    hidden: true,
  }),
  'payroll.weeklyOffRule': setting({
    key: 'payroll.weeklyOffRule',
    label: 'Weekly off pay',
    description: 'Lenient (old system): a weekly off is paid if the day before or after was worked or paid. Always: always paid. Strict: paid only if both neighbours were worked or paid.',
    group: 'Payroll',
    schema: z.enum(['LENIENT', 'ALWAYS', 'STRICT']),
    choices: { LENIENT: 'Lenient (old system)', ALWAYS: 'Always paid', STRICT: 'Strict' },
    default: 'LENIENT' as 'LENIENT' | 'ALWAYS' | 'STRICT',
  }),
  'costing.labourMethod': setting({
    key: 'costing.labourMethod',
    label: 'Labour cost of production',
    description: 'Each worker: their monthly salary (basic + allowances) ÷ the standard hours of a month, for every hour they log on a job. Flat: one rate for everybody (the field below). Production Setup rates: the worker’s own rate, else their section’s rate (Furniture › Configurations › Production Setup › Labour rates), else their salary.',
    group: 'Costing',
    schema: z.enum(['EMPLOYEE', 'FLAT', 'SETUP']),
    choices: { EMPLOYEE: 'Each worker’s own hourly cost', FLAT: 'One flat rate per hour', SETUP: 'Production Setup rates (worker, else section)' },
    default: 'EMPLOYEE' as 'EMPLOYEE' | 'FLAT' | 'SETUP',
  }),
  'costing.monthlyHours': setting({
    key: 'costing.monthlyHours',
    label: 'Standard hours in a month',
    description: 'Divides a monthly salary into an hourly cost (26 days × 8 hours = 208).',
    group: 'Costing',
    schema: z.coerce.number().min(100).max(400),
    unit: 'hours',
    default: 208 as number,
  }),
  'costing.overheadPerHourFurniture': setting({
    key: 'costing.overheadPerHourFurniture',
    label: 'Furniture overhead per labour hour',
    description: 'Factory overhead charged to furniture jobs for every hour worked (rent, power, depreciation, supervision…). Use Costing › Overhead Rate to work it out; confirm with the accountant.',
    group: 'Costing',
    schema: z.coerce.number().min(0).max(10000),
    unit: 'AED / hour',
    default: 0 as number,
  }),
  'costing.overheadPerMattress': setting({
    key: 'costing.overheadPerMattress',
    label: 'Mattress overhead per mattress',
    description: 'Factory overhead charged to each finished mattress (mattress production records units, not hours).',
    group: 'Costing',
    schema: z.coerce.number().min(0).max(10000),
    unit: 'AED / mattress',
    default: 0 as number,
  }),
  'costing.labourPerMattress': setting({
    key: 'costing.labourPerMattress',
    label: 'Mattress labour per mattress',
    description: 'Labour charged to each finished mattress (mattress section wages ÷ normal output). Use Costing › Overhead Rate to work it out.',
    group: 'Costing',
    schema: z.coerce.number().min(0).max(10000),
    unit: 'AED / mattress',
    default: 0 as number,
  }),
  'costing.overheadAccountIds': setting({
    key: 'costing.overheadAccountIds',
    label: 'Overhead accounts',
    description: 'Ledger accounts (or groups) whose spending is production overhead — chosen on Costing › Overhead Rate.',
    group: 'Costing',
    schema: z.array(z.string().uuid()).max(200),
    default: [] as string[],
    hidden: true,
  }),
  'costing.varianceAlertPct': setting({
    key: 'costing.varianceAlertPct',
    label: 'Flag cost variances above',
    description: 'Jobs and production orders whose actual cost is this much above (or below) standard are flagged in the variance report.',
    group: 'Costing',
    schema: z.coerce.number().min(1).max(100),
    unit: '%',
    default: 10 as number,
  }),
  'mfg.labourRatePerHour': setting({
    key: 'mfg.labourRatePerHour',
    label: 'Flat labour rate per hour (AED)',
    description: 'Used when “Labour cost of production” is set to one flat rate, and for workers with no salary on record.',
    group: 'Manufacturing',
    schema: z.coerce.number().min(0).max(10000),
    unit: 'AED / hour',
    default: 0 as number,
  }),
  'mfg.qcRequired': setting({
    key: 'mfg.qcRequired',
    label: 'Quality check before finished goods',
    description: 'When on, a furniture job that finishes its last operation waits for a quality check; only a pass receives the finished goods. Rework adds operations and checks again.',
    group: 'Manufacturing',
    schema: z.boolean(),
    default: true as boolean,
  }),
  // ---------- Furniture planning rules (kept on Production Setup › Planning rules) ----------
  'mfg.plan.jobModes': setting({
    key: 'mfg.plan.jobModes',
    label: 'Ways to make jobs from an order',
    description: 'Per item: one job for each order line. Per sub-category: one job for all Beds, all Side Tables … Whole order: one job for every furniture line.',
    group: 'Furniture planning',
    schema: z.array(z.enum(['ITEM', 'SUBCATEGORY', 'ORDER'])).min(1, 'Allow at least one way'),
    default: ['ITEM', 'SUBCATEGORY', 'ORDER'] as ('ITEM' | 'SUBCATEGORY' | 'ORDER')[],
    hidden: true,
  }),
  'mfg.plan.difficultyLabels': setting({
    key: 'mfg.plan.difficultyLabels',
    label: 'Difficulty labels',
    description: 'A label for the whole job, for reports only: it never changes minutes or pay.',
    group: 'Furniture planning',
    schema: z.array(z.string().trim().min(1).max(20)).min(1).max(10),
    default: ['A', 'B', 'C'] as string[],
    hidden: true,
  }),
  'mfg.plan.unusualMinutesPct': setting({
    key: 'mfg.plan.unusualMinutesPct',
    label: 'Warn when minutes are unusual by more than',
    description: 'Minutes per piece far from the same operation on similar past jobs get a warning (never a block).',
    group: 'Furniture planning',
    schema: z.coerce.number().int().min(10).max(1000),
    unit: '%',
    default: 100 as number,
    hidden: true,
  }),
  'mfg.plan.allDetailedRequired': setting({
    key: 'mfg.plan.allDetailedRequired',
    label: 'Every detailed operation must be chosen',
    description: 'In a Detailed plan, the planner must keep every detailed operation of the category. A category can override this.',
    group: 'Furniture planning',
    schema: z.boolean(),
    default: false as boolean,
    hidden: true,
  }),
  'mfg.plan.allowOverlapDefault': setting({
    key: 'mfg.plan.allowOverlapDefault',
    label: 'Bookings may overlap the previous operation (default)',
    description: 'Normally a booking starts only after the previous operation of the same item ends. Operations marked “can overlap” always may.',
    group: 'Furniture planning',
    schema: z.boolean(),
    default: false as boolean,
    hidden: true,
  }),
  'mfg.plan.sundayOvertime': setting({
    key: 'mfg.plan.sundayOvertime',
    label: 'Sundays can be enabled as overtime',
    description: 'Sundays are off; when on, the planner may enable a Sunday for an employee as overtime.',
    group: 'Furniture planning',
    schema: z.boolean(),
    default: true as boolean,
    hidden: true,
  }),
  'mfg.plan.shiftTimesOnly': setting({
    key: 'mfg.plan.shiftTimesOnly',
    label: 'Booking times come from the shift',
    description: 'Times offered are the employee’s shift (no free typing of start / end).',
    group: 'Furniture planning',
    schema: z.boolean(),
    default: true as boolean,
    hidden: true,
  }),
  'mfg.plan.materialApproval': setting({
    key: 'mfg.plan.materialApproval',
    label: 'Material plan needs approval',
    description: 'A job’s material plan is sent for approval before requisitions can be made. Approvers: roles with “Approve material plans”.',
    group: 'Furniture planning',
    schema: z.boolean(),
    default: true as boolean,
    hidden: true,
  }),
  'mfg.plan.replanMode': setting({
    key: 'mfg.plan.replanMode',
    label: 'Changing a plan that has bookings or material',
    description: 'Ask: show what the change undoes and wait for the planner to confirm. Undo automatically: undo the affected bookings at once (approved material is never undone; it needs a revision).',
    group: 'Furniture planning',
    schema: z.enum(['ASK', 'AUTO_UNDO']),
    choices: { ASK: 'Ask first (show what it undoes)', AUTO_UNDO: 'Undo affected bookings automatically' },
    default: 'ASK' as 'ASK' | 'AUTO_UNDO',
    hidden: true,
  }),
  'mfg.plan.defaultPlanMode': setting({
    key: 'mfg.plan.defaultPlanMode',
    label: 'Default plan type',
    description: 'Offered first when a job gets its category: Simple (the main operations) or Detailed (chosen from the full list). A category can set its own.',
    group: 'Furniture planning',
    schema: z.enum(['SIMPLE', 'DETAILED']),
    choices: { SIMPLE: 'Simple', DETAILED: 'Detailed' },
    default: 'SIMPLE' as 'SIMPLE' | 'DETAILED',
    hidden: true,
  }),
  'mfg.plan.dispatchDays': setting({
    key: 'mfg.plan.dispatchDays',
    label: 'Days from the last factory operation to delivery',
    description: 'Used for the realistic finish of an order while no delivery note exists (Dispatch is a milestone of the Delivery module).',
    group: 'Furniture planning',
    schema: z.coerce.number().int().min(0).max(60),
    unit: 'days',
    default: 1 as number,
    hidden: true,
  }),
  'mfg.plan.installDays': setting({
    key: 'mfg.plan.installDays',
    label: 'Days for installation at the site after delivery',
    description: 'Used for the realistic finish of an order with site operations while no installation visit is scheduled.',
    group: 'Furniture planning',
    schema: z.coerce.number().int().min(0).max(60),
    unit: 'days',
    default: 1 as number,
    hidden: true,
  }),
  'mfg.plan.timeLimits': setting({
    key: 'mfg.plan.timeLimits',
    label: 'Time limits per planning step (days)',
    description: 'Route & minutes from the order reaching production; people & dates and materials from route & minutes being fixed; material approval from sending. Late tasks warn and the production manager is told once.',
    group: 'Furniture planning',
    schema: z.object({ ROUTE: z.coerce.number().min(0).max(60), PEOPLE: z.coerce.number().min(0).max(60), MATERIALS: z.coerce.number().min(0).max(60), APPROVE_MATERIAL: z.coerce.number().min(0).max(60) }),
    default: { ROUTE: 1, PEOPLE: 2, MATERIALS: 2, APPROVE_MATERIAL: 1 } as { ROUTE: number; PEOPLE: number; MATERIALS: number; APPROVE_MATERIAL: number },
    hidden: true,
  }),
  'mfg.plan.materialLeadDays': setting({
    key: 'mfg.plan.materialLeadDays',
    label: 'Material lead time (days)',
    description: 'Added to the suggested completion date of an inquiry until items carry their own lead time.',
    group: 'Furniture planning',
    schema: z.coerce.number().int().min(0).max(120),
    unit: 'days',
    default: 7 as number,
    hidden: true,
  }),
  'mfg.substituteAutoPct': setting({
    key: 'mfg.substituteAutoPct',
    label: 'Substitute without approval up to',
    description: 'The store may issue a substitute item straight away when it is in the same category as the requested item and costs no more than this much above it; otherwise the section head approves.',
    group: 'Manufacturing',
    schema: z.coerce.number().min(0).max(100),
    unit: '%',
    default: 5 as number,
  }),
  'installation.autoCreate': setting({
    key: 'installation.autoCreate',
    label: 'Installation orders from deliveries',
    description: 'When a delivery note is posted, an installation order is made for its items that need fixing (set on the product or its category).',
    group: 'Installation',
    schema: z.boolean(),
    default: true as boolean,
  }),
  'installation.visitHours': setting({
    key: 'installation.visitHours',
    label: 'Default visit length',
    description: 'How long a new installation visit is planned for, until the planner changes it.',
    group: 'Installation',
    schema: z.coerce.number().min(0.5).max(24),
    unit: 'hours',
    default: 4 as number,
  }),
  'installation.dueDays': setting({
    key: 'installation.dueDays',
    label: 'Install within',
    description: 'An installation not done this many days after delivery shows as overdue.',
    group: 'Installation',
    schema: z.coerce.number().int().min(1).max(60),
    unit: 'days',
    default: 3 as number,
  }),
  'presales.advancePct': setting({
    key: 'presales.advancePct',
    label: 'Advance before release',
    description: 'Share of the order total the customer must have paid (receipts with the order number as reference) before it can be released to production. A manager may override with a reason.',
    group: 'Pre-sales',
    schema: z.coerce.number().min(0).max(100),
    unit: '%',
    default: 50 as number,
  }),
  'presales.marginPct': setting({
    key: 'presales.marginPct',
    label: 'Default margin on cost sheets',
    description: 'Starting margin when a cost sheet is made; the price is cost ÷ (1 − margin).',
    group: 'Pre-sales',
    schema: z.coerce.number().min(0).max(90),
    unit: '%',
    default: 35 as number,
  }),
  'presales.minMarginPct': setting({
    key: 'presales.minMarginPct',
    label: 'Minimum margin without approval',
    description: 'Inquiry Studio pre-costing: a line priced below this margin needs the approval of the "Approve low margin" owner before the quotation.',
    group: 'Pre-sales',
    schema: z.coerce.number().min(-100).max(90),
    unit: '%',
    default: 0 as number,
  }),
  'presales.quoteValidDays': setting({
    key: 'presales.quoteValidDays',
    label: 'Quotation valid for',
    description: 'Inquiry Studio: a quotation is valid this many days from its date (it can be changed per quotation). An expired quotation is quoted again as a new revision.',
    group: 'Pre-sales',
    schema: z.coerce.number().int().min(1).max(180),
    unit: 'days',
    default: 15 as number,
  }),
  'presales.inqTimeLimits': setting({
    key: 'presales.inqTimeLimits',
    label: 'Inquiry Studio time limits',
    description: 'Days an inquiry may stay at each gate before it shows as overdue (and its owners are reminded).',
    group: 'Pre-sales',
    schema: z.object({ INQUIRY: z.coerce.number().min(0).max(90), DRAWINGS: z.coerce.number().min(0).max(90), REVIEW: z.coerce.number().min(0).max(90), DISPATCH: z.coerce.number().min(0).max(90), COSTING: z.coerce.number().min(0).max(90), QUOTATION: z.coerce.number().min(0).max(90), ORDER: z.coerce.number().min(0).max(90) }),
    default: { INQUIRY: 1, DRAWINGS: 3, REVIEW: 2, DISPATCH: 1, COSTING: 2, QUOTATION: 15, ORDER: 3 } as { INQUIRY: number; DRAWINGS: number; REVIEW: number; DISPATCH: number; COSTING: number; QUOTATION: number; ORDER: number },
    hidden: true,
  }),
  'presales.inqReviewMode': setting({
    key: 'presales.inqReviewMode',
    label: 'Inquiry Studio: who does the production review',
    description: 'Section heads: the production planner picks the category and operations, then each section head fills and approves the minutes and material of their own operations. One planner: the production planner fills and approves everything.',
    group: 'Pre-sales',
    schema: z.enum(['SECTIONS', 'PLANNER']),
    choices: { SECTIONS: 'Section heads — each fills and approves their own operations', PLANNER: 'One production planner — fills and approves everything' },
    default: 'SECTIONS' as 'SECTIONS' | 'PLANNER',
    hidden: true,
  }),
  'presales.dispatchDays': setting({
    key: 'presales.dispatchDays',
    label: 'Days from finished to delivered',
    description: 'Added to the production finish date to propose a delivery date (packing, dispatch, transport).',
    group: 'Pre-sales',
    schema: z.coerce.number().int().min(0).max(60),
    unit: 'days',
    default: 2 as number,
  }),
  'presales.holdDays': setting({
    key: 'presales.holdDays',
    label: 'Hold capacity for',
    description: 'A tentative production slot for a quoted enquiry is kept this many days (or until the quotation expires), then released.',
    group: 'Pre-sales',
    schema: z.coerce.number().int().min(1).max(90),
    unit: 'days',
    default: 14 as number,
  }),
  'service.warrantyMonths': setting({
    key: 'service.warrantyMonths',
    label: 'Standard warranty',
    description: 'Warranty on finished goods when neither the product nor its category sets one. It runs from the installation handover, or the delivery when nothing is installed.',
    group: 'After-Sales',
    schema: z.coerce.number().int().min(0).max(240),
    unit: 'months',
    default: 12 as number,
  }),
  'service.responseHours': setting({
    key: 'service.responseHours',
    label: 'First response within',
    description: 'A new ticket nobody has answered (note, assessment or visit) within this time shows as late.',
    group: 'After-Sales',
    schema: z.coerce.number().min(1).max(240),
    unit: 'hours',
    default: 24 as number,
  }),
  'service.resolveDays': setting({
    key: 'service.resolveDays',
    label: 'Resolve within',
    description: 'A ticket not resolved this many days after it was opened shows as overdue (urgent tickets: half).',
    group: 'After-Sales',
    schema: z.coerce.number().int().min(1).max(90),
    unit: 'days',
    default: 7 as number,
  }),
  'alerts.enabled': setting({
    key: 'alerts.enabled',
    label: 'Smart alerts',
    description: 'Every 30 minutes the system looks for late jobs, promises at risk, material shortages, plan conflicts, QC rework, extra material, high rejects, drawings waiting and overdue installations, and sends one grouped notice per problem type to the people who handle it — at most once a day each.',
    group: 'Alerts',
    schema: z.boolean(),
    default: true as boolean,
  }),
  'alerts.digestHour': setting({
    key: 'alerts.digestHour',
    label: 'Daily digest time',
    description: 'Hour of the day (UAE time) when everyone gets a one-message summary of what needs attention in their areas.',
    group: 'Alerts',
    schema: z.coerce.number().int().min(0).max(23),
    unit: 'o’clock',
    default: 7 as number,
  }),
  'mfg.fgWarehouseCode': setting({
    key: 'mfg.fgWarehouseCode',
    label: 'Finished goods warehouse (code)',
    description: 'Warehouse that receives finished furniture when a production job is complete.',
    group: 'Manufacturing',
    schema: z.string().trim().min(1).max(20),
    default: 'FG-MAIN' as string,
  }),
  'attendance.correctionApproval': setting({
    key: 'attendance.correctionApproval',
    label: 'Attendance corrections need approval',
    description: 'When on, the corrections ticked below wait for someone with "Approve attendance corrections" (never the person who made them) before they change the day.',
    group: 'Attendance',
    schema: z.boolean(),
    default: false as boolean,
  }),
  'attendance.approve.addPunch': setting({
    key: 'attendance.approve.addPunch',
    label: 'Approval needed: add a missing punch',
    description: 'Only when corrections need approval.',
    group: 'Attendance',
    schema: z.boolean(),
    default: true as boolean,
  }),
  'attendance.approve.removePunch': setting({
    key: 'attendance.approve.removePunch',
    label: 'Approval needed: remove a wrong punch',
    description: 'Only when corrections need approval. The punch stays in the raw log and is ignored.',
    group: 'Attendance',
    schema: z.boolean(),
    default: true as boolean,
  }),
  'attendance.approve.movePunch': setting({
    key: 'attendance.approve.movePunch',
    label: 'Approval needed: change a punch time',
    description: 'Only when corrections need approval.',
    group: 'Attendance',
    schema: z.boolean(),
    default: true as boolean,
  }),
  'attendance.approve.day': setting({
    key: 'attendance.approve.day',
    label: 'Approval needed: set the whole day',
    description: 'Only when corrections need approval (absent, or In / Out set by hand).',
    group: 'Attendance',
    schema: z.boolean(),
    default: true as boolean,
  }),
  'attendance.approvalAfterDays': setting({
    key: 'attendance.approvalAfterDays',
    label: 'Approval only for days older than (days)',
    description: '0 = every day. For example 2: today\'s and yesterday\'s forgotten punches are fixed at once; older days need approval.',
    group: 'Attendance',
    schema: z.coerce.number().int().min(0).max(60),
    default: 0 as number,
  }),
  'payroll.overtimeBase': setting({
    key: 'payroll.overtimeBase',
    label: 'Overtime & late rate',
    description: 'Hourly rate for overtime and late deductions from the gross salary (old system) or from basic salary (UAE labour law basis for overtime).',
    group: 'Payroll',
    schema: z.enum(['GROSS', 'BASIC']),
    choices: { GROSS: 'Gross salary (old system)', BASIC: 'Basic salary' },
    default: 'GROSS' as 'GROSS' | 'BASIC',
  }),
  'payroll.holidayRule': setting({
    key: 'payroll.holidayRule',
    label: 'Public holiday pay',
    description: 'Linked (old system): paid if the nearest working day before or after was worked or paid. Always: always paid.',
    group: 'Payroll',
    schema: z.enum(['LINKED', 'ALWAYS']),
    choices: { LINKED: 'Linked (old system)', ALWAYS: 'Always paid' },
    default: 'LINKED' as 'LINKED' | 'ALWAYS',
  }),
  'planning.furnitureFrozenDays': setting({
    key: 'planning.furnitureFrozenDays',
    label: 'Furniture: frozen planning window (days)',
    description: 'The next N working days of the furniture plan are locked: automatic re-planning never touches them, and manual changes inside need a supervisor. 0 = no frozen window.',
    group: 'Production planning',
    schema: z.coerce.number().int().min(0).max(30),
    unit: 'days',
    default: 2 as number,
  }),
  'planning.mattressFrozenDays': setting({
    key: 'planning.mattressFrozenDays',
    label: 'Mattress: frozen planning window (days)',
    description: 'Same as above for the mattress production plan.',
    group: 'Production planning',
    schema: z.coerce.number().int().min(0).max(30),
    unit: 'days',
    default: 1 as number,
  }),
  'planning.autoReplan': setting({
    key: 'planning.autoReplan',
    label: 'Automatic re-planning',
    description: 'What the system does when something changes (absence, late operation, late material, rework). Suggest: proposes a new plan for a planner to publish. Within rules: applies it automatically outside the frozen window and never past committed delivery dates; everything else is proposed. Off: only flags the problem.',
    group: 'Production planning',
    schema: z.enum(['OFF', 'SUGGEST', 'AUTO_WITHIN_RULES']),
    choices: { OFF: 'Off — only flag problems', SUGGEST: 'Suggest — a planner publishes', AUTO_WITHIN_RULES: 'Automatic within the rules' },
    default: 'SUGGEST' as 'OFF' | 'SUGGEST' | 'AUTO_WITHIN_RULES',
  }),
  'planning.committedDateChange': setting({
    key: 'planning.committedDateChange',
    label: 'Changes that move a committed delivery date',
    description: 'Approval: the change waits for an approver before it takes effect. Warn: allowed with a reason and the salesperson is notified. Block: not allowed; the plan must be fitted around the date.',
    group: 'Production planning',
    schema: z.enum(['APPROVAL', 'WARN', 'BLOCK']),
    choices: { APPROVAL: 'Needs approval', WARN: 'Allowed with a reason (salesperson notified)', BLOCK: 'Not allowed' },
    default: 'APPROVAL' as 'APPROVAL' | 'WARN' | 'BLOCK',
  }),
  'planning.priorityRules': setting({
    key: 'planning.priorityRules',
    label: 'Scheduling priority (in order)',
    description: 'How the scheduler decides which work goes first when capacity is short. The first rule wins; later rules break ties.',
    group: 'Production planning',
    choices: PLANNING_PRIORITY_LABELS,
    schema: z.array(z.enum(PLANNING_PRIORITY_RULES)).min(1).max(PLANNING_PRIORITY_RULES.length).refine((a) => new Set(a).size === a.length, 'Each rule once'),
    default: ['COMMITTED_DATE', 'RUSH', 'ADVANCE_PAID', 'VIP', 'ORDER_AGE'] as PlanningPriorityRule[],
  }),
  'planning.mattressDailyCapacity': setting({
    key: 'planning.mattressDailyCapacity',
    label: 'Mattress: daily capacity (units)',
    description: 'How many mattresses the line can make in a day. The production plan shows each day against it; 0 = not set.',
    group: 'Production planning',
    schema: z.coerce.number().int().min(0).max(100000),
    unit: 'units / day',
    default: 0 as number,
  }),
  'planning.skillsRule': setting({
    key: 'planning.skillsRule',
    label: 'Use the skills matrix',
    description: 'Prefer: Auto-plan picks qualified people first and the plan check warns about others. Require: only qualified people are suggested. Off: ignore skills. An operation with no skills recorded is open to everyone in its section.',
    group: 'Production planning',
    schema: z.enum(['OFF', 'PREFER', 'REQUIRE']),
    choices: { OFF: 'Off', PREFER: 'Prefer qualified people', REQUIRE: 'Only qualified people' },
    default: 'PREFER' as 'OFF' | 'PREFER' | 'REQUIRE',
  }),
  'planning.allowOverbooking': setting({
    key: 'planning.allowOverbooking',
    label: 'Allow overtime beyond the shift',
    description: 'Lets planners book people past their shift end (overtime). Double booking the same person at the same time is never allowed.',
    group: 'Production planning',
    schema: z.boolean(),
    default: false,
  }),
} as const;

export type SettingKey = keyof typeof SETTINGS;
export type SettingValues = { [K in SettingKey]: (typeof SETTINGS)[K]['default'] };
/** Settings changed only through their module's own process, never directly. */
export const READ_ONLY_SETTINGS: readonly SettingKey[] = ['inventory.valuationMode', 'inventory.costMethod', 'inventory.orderStockCost'];

export const updateSettingsSchema = z
  .record(z.string(), z.unknown())
  .superRefine((obj, ctx) => {
    for (const [k, v] of Object.entries(obj)) {
      const def = SETTINGS[k as SettingKey];
      if (!def) ctx.addIssue({ code: 'custom', path: [k], message: 'Unknown setting' });
      else if (READ_ONLY_SETTINGS.includes(k as SettingKey)) ctx.addIssue({ code: 'custom', path: [k], message: 'This setting cannot be changed here' });
      else if (!def.schema.safeParse(v).success) ctx.addIssue({ code: 'custom', path: [k], message: 'Invalid value' });
    }
  });

// ---------- Fiscal years & periods ----------

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date');

export const createFiscalYearSchema = z.object({
  code: z.string().trim().regex(/^FY\d{4}(-\d{2})?$/, 'Use the form FY2026 or FY2026-27'),
  startDate: isoDate,
});
export type CreateFiscalYearInput = z.infer<typeof createFiscalYearSchema>;

export const periodActionSchema = z.object({
  version: z.number().int(),
  comment: z.string().trim().max(500).optional().default(''),
});

// ---------- Back-dating ----------

const docTypeKey = z.string().refine((k) => k === '*' || !!DOCUMENT_TYPES_BY_KEY[k], 'Select a document type');

export const backdateRuleSchema = z.object({
  roleId: z.string().uuid('Select a role'),
  docType: docTypeKey,
  maxDays: z.coerce.number({ error: 'Enter the number of days' }).int('Whole days only').min(0, 'Cannot be negative').max(3650, 'At most 3650 days'),
});
export type BackdateRuleInput = z.infer<typeof backdateRuleSchema>;

export const backdateGrantSchema = z
  .object({
    userId: z.string().uuid('Select a user'),
    docType: docTypeKey,
    allowFrom: isoDate,
    expiresAt: isoDate,
    reason: z.string().trim().min(5, 'Give a reason (at least 5 characters)').max(300),
  })
  .refine((v) => !v.allowFrom || !v.expiresAt || v.expiresAt >= new Date().toISOString().slice(0, 10), {
    path: ['expiresAt'],
    message: 'Expiry must be today or later',
    ...always,
  })
  .refine((v) => !v.allowFrom || v.allowFrom <= new Date().toISOString().slice(0, 10), {
    path: ['allowFrom'],
    message: 'Allow-from date must be in the past',
    ...always,
  });
export type BackdateGrantInput = z.infer<typeof backdateGrantSchema>;

// ---------- Workflow ----------

export const workflowActionSchema = z.object({
  version: z.number().int(),
  comment: z.string().trim().max(1000).optional().default(''),
});
export type WorkflowActionInput = z.infer<typeof workflowActionSchema>;

// ---------- Response types ----------

export interface DocumentSummary {
  id: string;
  docType: string;
  docTypeLabel: string;
  module: string;
  screen: string;
  number: string | null;
  legacyNumber: string | null;
  docDate: string;
  status: DocStatus;
  title: string;
  partyName: string | null;
  amount: number | null;
  currency: string;
  createdBy: { id: string; name: string } | null;
  submittedAt: string | null;
  version: number;
  /** Actions the current user may take now. */
  actions: WorkflowAction[];
  /** Multi-level approval progress (only when an approval policy has levels for this document). */
  approval?: { levels: { name: string; done: boolean }[]; current: string | null } | null;
  /** Hours since submission while pending. */
  waitingHours?: number | null;
  /** Pending longer than the policy's reminder time. */
  overdue?: boolean;
}

// ---------- Approval policies ----------

export interface ApprovalLevel {
  name: string;
  roleIds: string[];
  userIds: string[];
  /** Level applies only when the document amount is at least this (null = always). */
  minAmount: number | null;
}

export interface ApprovalPolicyDef {
  docType: string;
  required: boolean;
  allowSelfApproval: boolean;
  reminderHours: number | null;
  levels: ApprovalLevel[];
  notes: string;
  version: number;
}

export const approvalPolicySchema = z
  .object({
    required: z.boolean(),
    allowSelfApproval: z.boolean().default(false),
    reminderHours: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number().int().min(1).max(24 * 30).nullable()).default(null),
    levels: z
      .array(
        z.object({
          name: z.string().trim().min(2, 'Name the level').max(60),
          roleIds: z.array(z.string().uuid()).max(30).default([]),
          userIds: z.array(z.string().uuid()).max(30).default([]),
          minAmount: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number().min(0).max(1_000_000_000).nullable()).default(null),
        }),
      )
      .max(3, 'At most 3 levels')
      .default([]),
    notes: z.string().trim().max(500).default(''),
  })
  .superRefine((v, ctx) => {
    if (!v.required && v.levels.length) ctx.addIssue({ code: 'custom', path: ['levels'], message: 'Remove the levels, or require approval' });
    v.levels.forEach((l, i) => {
      const prev = v.levels.slice(0, i).map((x) => x.minAmount ?? 0);
      if (l.minAmount != null && prev.some((p) => p > l.minAmount!)) ctx.addIssue({ code: 'custom', path: ['levels', i, 'minAmount'], message: 'Should not be below an earlier level’s minimum' });
    });
  });
export type ApprovalPolicyInput = z.infer<typeof approvalPolicySchema>;

export interface ApprovalPolicyRow {
  docType: string;
  label: string;
  module: string;
  /** null = no policy saved: the standard single approval by permission. */
  policy: ApprovalPolicyDef | null;
  pending: number;
  postOnApprove: boolean;
  approvers: number;
}

export interface DocumentEventItem {
  /** For an edit: what changed on the lines (added / removed / changed). */
  lineChanges?: string[];
  id: string;
  action: string;
  fromStatus: DocStatus | null;
  toStatus: DocStatus | null;
  comment: string | null;
  actorName: string | null;
  at: string;
}

export interface InboxSummary {
  total: number;
  byType: { docType: string; label: string; count: number }[];
}

export interface FiscalPeriodItem {
  id: string;
  number: number;
  name: string;
  startDate: string;
  endDate: string;
  status: 'OPEN' | 'CLOSED';
  closedAt: string | null;
  version: number;
}

export interface FiscalYearItem {
  id: string;
  code: string;
  startDate: string;
  endDate: string;
  periods: FiscalPeriodItem[];
}

export interface SequenceItem {
  docType: string;
  label: string;
  prefix: string;
  period: string;
  lastValue: number;
  nextNumber: string;
}

export interface AttachmentItem {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedBy: string | null;
}

export interface MigrationRunItem {
  id: string;
  importer: string;
  status: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';
  dryRun: boolean;
  startedAt: string | null;
  finishedAt: string | null;
  readCount: number;
  writtenCount: number;
  rejectCount: number;
  warningCount: number;
  message: string | null;
  checks: { name: string; legacyValue: number; newValue: number; difference: number; passed: boolean; note: string | null }[];
}

export interface ImporterInfo {
  key: string;
  label: string;
  description: string;
  dependsOn: string[];
}

/** "14:05" → "02:05 PM" (owner, 2026-10-26: 12-hour time everywhere). Inputs stay 24-hour. */
export function clock12(hhmm: string | null | undefined): string {
  if (!hhmm || !/^\d{1,2}:\d{2}/.test(hhmm)) return hhmm ?? '';
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return `${String(h % 12 || 12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}
